import { useMutation } from '@apollo/client';
import { Form, Input, Modal, notification } from 'antd';
import React, { useCallback, useEffect } from 'react';
import SelectUser from '../users/SelectUser';
import { updatePartnerOnboardingMutation } from './constants';
import SelectCoordinator from './SelectCoordinator';

function EditPartnerOnboardingModal({ record, visible, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [updateOnboarding, { loading }] = useMutation(
    updatePartnerOnboardingMutation,
  );
  // Partner type is fixed at creation: a partner needing a new type gets a new
  // onboarding round, not an edit.
  const isFieldWorker = record?.partnerType === 'F';

  useEffect(() => {
    if (record && visible) {
      form.setFieldsValue({
        notes: record.notes || undefined,
        assignedStaffIds: record.assignedStaffIds || [],
        coordinatorContactId: record.coordinatorContactId || undefined,
      });
    }
  }, [form, record, visible]);

  const handleFinish = useCallback(
    async (values) => {
      try {
        await updateOnboarding({
          variables: {
            partnerOnboarding: {
              _id: record._id,
              notes: values.notes || undefined,
              assignedStaffIds: values.assignedStaffIds || [],
              // Coordinator is only meaningful for a Field Worker; a Coordinator's own
              // self-reference is set server-side, and no other type has one.
              coordinatorContactId: isFieldWorker
                ? values.coordinatorContactId
                : undefined,
            },
          },
        });
        notification.success({ message: 'Updated' });
        onSuccess();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [updateOnboarding, record, onSuccess, isFieldWorker],
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Edit Partner Onboarding"
      onOk={() => form.submit()}
      okText="Save"
      confirmLoading={loading}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        {isFieldWorker && (
          <Form.Item
            name="coordinatorContactId"
            label="Coordinator"
            rules={[
              {
                required: true,
                message: 'A coordinator is required for field workers',
              },
            ]}
            extra="For correcting a missing or wrong coordinator. To move a field worker to a different coordinator, cancel this onboarding and start a new one under them. The field worker inherits this coordinator's region."
          >
            <SelectCoordinator skip={!isFieldWorker} />
          </Form.Item>
        )}
        <Form.Item name="assignedStaffIds" label="Assigned Staff">
          <SelectUser mode="multiple" />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={4} placeholder="Internal notes" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default EditPartnerOnboardingModal;
